import { useEffect, useState } from "react";
import { useApiClient } from "../api/useApiClient";
import type { LineaFactura } from "../types/reviewItem";

/**
 * Fila expandible con el detalle línea por línea del CFE
 * (GET /review-items/{id}/lineas). Se monta solo cuando la fila padre está
 * expandida — el fetch es on-demand, no precargado para toda la lista.
 */
export function LineasRow({ reviewItemId, colSpan }: { reviewItemId: string; colSpan: number }) {
  const api = useApiClient();
  const [lineas, setLineas] = useState<LineaFactura[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLineas(null);
    setError(null);
    api
      .obtenerLineas(reviewItemId)
      .then((resultado) => {
        if (!cancelado) setLineas(resultado);
      })
      .catch((err) => {
        if (!cancelado) setError(err instanceof Error ? err.message : "Error al cargar las líneas.");
      });
    return () => {
      cancelado = true;
    };
  }, [api, reviewItemId]);

  return (
    <tr className="lineas-row">
      <td colSpan={colSpan}>
        {error && <p className="error-text">{error}</p>}
        {!error && lineas === null && <p className="subtitle">Cargando líneas...</p>}
        {lineas && lineas.length === 0 && <p className="subtitle">Sin líneas registradas.</p>}
        {lineas && lineas.length > 0 && (
          <table className="lineas-table">
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Cantidad</th>
                <th>Precio unitario</th>
                <th>IVA</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((linea, i) => (
                <tr key={i}>
                  <td>{linea.concepto}</td>
                  <td className="mono">{Number(linea.cantidad)}</td>
                  <td className="mono">{Number(linea.precio_unitario).toFixed(2)}</td>
                  <td className="mono">{linea.tax_code ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
}

/** Botón toggle consistente para expandir/contraer la fila de líneas. */
export function LineasToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="btn-ghost" onClick={onToggle}>
      {expanded ? "▾ Líneas" : "▸ Líneas"}
    </button>
  );
}
